import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import styles from './styles/AccountScenarios.module.css';
import * as ScenarioAPI from '../../services/ScenarioServices';
import ScenarioCard from '../mainlayout/body/ScenarioCard';
import DeleteScenarioModal from './DeleteScenarioModal';
import CopyScenarioModal from './CopyScenarioModal';
import CategoryModal from './CategoryModal';
import SortFilterDropdown from '../common/SortFilterDropdown';
import { useNavigate } from 'react-router-dom';
import PermissionsContext from '../../context/PermissionsContext';

// Sorts are applied client-side (a profile's scenarios are fetched in one go),
// so values are comparator keys here rather than API params. Default order is
// the fetch order: newest first.
const SORT_OPTIONS = [
    { value: 'createdAt', label: 'Date Created' },
    { value: 'title', label: 'Title' },
    { value: 'views', label: 'Views' },
    { value: 'likes', label: 'Likes' },
];

// Ascending comparators; direction is applied by flipping the sign.
const SORTERS = {
    createdAt: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    title: (a, b) => a.title.localeCompare(b.title),
    views: (a, b) => (a.views || 0) - (b.views || 0),
    likes: (a, b) => (a.likedBy?.length || 0) - (b.likedBy?.length || 0),
};

const isPubliclyVisible = (scenario) =>
    scenario.isPublished && (scenario.isHidden === false || scenario.isHidden == null);

/**
 * Scenario grid for the profile page, covering both the "Scenarios" tab
 * (scenarios the account owns) and the "Shared" tab (scenarios the account
 * collaborates on) via the `isShared` flag.
 */
const AccountScenarioGrid = ({ userIsAccountOwner, accountOwner, setAccountOwner, isShared = false }) => {
    const { setScenarioCount } = useContext(PermissionsContext);
    const [scenarios, setScenarios] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortControls, setSortControls] = useState({ sortBy: '', sortOrder: 'desc', filterBy: '', creator: '', tags: [] });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [selectedScenarioId, setSelectedScenarioId] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [operationLoading, setOperationLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [editingCategory, setEditingCategory] = useState(null);
    const navigate = useNavigate();

    // The owned-scenarios count drives the navbar's scenario limit display and
    // only tracks scenarios the user created, never collaborations.
    const adjustOwnedScenarioCount = useCallback((delta) => {
        if (!isShared) {
            setScenarioCount(prev => prev + delta);
        }
    }, [isShared, setScenarioCount]);

    useEffect(() => {
        if (accountOwner?._id) {
            setInitialLoading(true);
            const request = isShared
                ? ScenarioAPI.getCollaborativeScenarios(accountOwner._id).then(data => data.response)
                : ScenarioAPI.getScenariosByUser(accountOwner._id);
            request
                .then(data => {
                    const sortedScenarios = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                    setScenarios(sortedScenarios);
                })
                .catch(error => console.error(`Error fetching scenarios: ${error}`))
                .finally(() => setInitialLoading(false));
        } else {
            setScenarios([]);
            setInitialLoading(false);
        }
    }, [accountOwner, isShared]);

    const hasDisplayableScenariosInitially = useMemo(() => {
        if (initialLoading || !scenarios || scenarios.length === 0) {
            return false;
        }
        if (userIsAccountOwner) {
            return scenarios.length > 0;
        }
        return scenarios.some(isPubliclyVisible);
    }, [scenarios, userIsAccountOwner, initialLoading]);

    const sortedAndFilteredScenarios = useMemo(() => {
        let scenariosToProcess = [...scenarios];

        if (selectedCategory) {
            scenariosToProcess = scenariosToProcess.filter(scenario =>
                selectedCategory.scenarios.includes(scenario._id)
            );
        } else {
            const query = searchQuery.trim().toLowerCase();
            if (query) {
                const tagText = (tagItem) => (typeof tagItem === 'string' ? tagItem : tagItem?.name ?? '').toLowerCase();
                if (query.startsWith('#')) {
                    const tag = query.slice(1);
                    scenariosToProcess = scenariosToProcess.filter(scenario =>
                        scenario.tags.some(tagItem => tagText(tagItem).includes(tag))
                    );
                } else {
                    scenariosToProcess = scenariosToProcess.filter(scenario =>
                        scenario.title.toLowerCase().includes(query) ||
                        (scenario.description && scenario.description.toLowerCase().includes(query)) ||
                        scenario.tags.some(tagItem => tagText(tagItem).includes(query))
                    );
                }
            }
        }

        const sorter = SORTERS[sortControls.sortBy];
        if (sorter) {
            const orderSign = sortControls.sortOrder === 'asc' ? 1 : -1;
            scenariosToProcess.sort((a, b) => sorter(a, b) * orderSign);
        }
        return scenariosToProcess;
    }, [searchQuery, scenarios, selectedCategory, sortControls]);

    const finalRenderScenarios = useMemo(() => {
        if (userIsAccountOwner) {
            return sortedAndFilteredScenarios;
        }
        return sortedAndFilteredScenarios.filter(isPubliclyVisible);
    }, [sortedAndFilteredScenarios, userIsAccountOwner]);

    const handleSearchChange = useCallback((event) => {
        setSearchQuery(event.target.value);
    }, []);

    const handleSortControlsChange = useCallback((partial) => {
        setSortControls(prev => ({ ...prev, ...partial }));
    }, []);

    const handleDeleteScenario = useCallback((event, scenarioId) => {
        event.stopPropagation();
        setSelectedScenarioId(scenarioId);
        setShowDeleteModal(true);
    }, []);

    const handleConfirmDelete = useCallback(() => {
        setOperationLoading(true);
        ScenarioAPI.deleteScenario(selectedScenarioId)
            .then(() => {
                setScenarios(prevScenarios => prevScenarios.filter(scenario => scenario._id !== selectedScenarioId));
                if (selectedCategory) {
                    setAccountOwner(prevOwner => ({
                        ...prevOwner,
                        categories: prevOwner.categories.map(cat =>
                            cat._id === selectedCategory._id
                            ? { ...cat, scenarios: cat.scenarios.filter(id => id !== selectedScenarioId) }
                            : cat
                        )
                    }));
                }
                adjustOwnedScenarioCount(-1);
            })
            .catch(error => console.error(`Error deleting scenario: ${error}`))
            .finally(() => {
                setOperationLoading(false);
                setSelectedScenarioId(null);
                setShowDeleteModal(false);
            });
    }, [selectedScenarioId, selectedCategory, setAccountOwner, adjustOwnedScenarioCount]);

    const handleCopyScenario = useCallback((event, scenarioId) => {
        event.stopPropagation();
        setSelectedScenarioId(scenarioId);
        setShowCopyModal(true);
    }, []);

    const runCopy = useCallback((copyRequest) => {
        setOperationLoading(true);
        copyRequest(selectedScenarioId)
            .then(data => {
                const newScenarioId = data._id;
                setSelectedScenarioId(null);
                setShowCopyModal(false);
                navigate(`/create/${newScenarioId}`);
                adjustOwnedScenarioCount(1);
            }).catch(error => {
                console.error('Error copying scenario:', error);
            }).finally(() => setOperationLoading(false));
    }, [selectedScenarioId, navigate, adjustOwnedScenarioCount]);

    const handleConfirmCopy = useCallback(() => runCopy(ScenarioAPI.copyScenario), [runCopy]);
    const handleConfirmCopyStructure = useCallback(() => runCopy(ScenarioAPI.copyScenarioStructure), [runCopy]);

    const handleCancelDelete = useCallback(() => {
        setSelectedScenarioId(null);
        setShowDeleteModal(false);
    }, []);

    const handleCancelCopy = useCallback(() => {
        setSelectedScenarioId(null);
        setShowCopyModal(false);
    }, []);

    const handleCategoryClick = useCallback((category) => {
        setSelectedCategory(prevCategory => (prevCategory && prevCategory._id === category._id) ? null : category);
        setSearchQuery('');
    }, []);

    const handleAddCategoryClick = useCallback(() => {
        setEditingCategory(null);
        setShowCategoryModal(true);
    }, []);

    const handleEditCategoryClick = useCallback((category) => {
        setEditingCategory(category);
        setShowCategoryModal(true);
    }, []);

    const handleCloseCategoryModal = useCallback(() => {
        setShowCategoryModal(false);
        setEditingCategory(null);
    }, []);

    if (initialLoading) {
        return (
            <div className={styles.loaderContainer} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <div className={styles.spinner}></div>
                <p style={{ marginLeft: '10px' }}>Loading scenarios...</p>
            </div>
        );
    }

    const emptyAccountMessage = isShared
        ? (userIsAccountOwner ? "You aren't a collaborator on any scenarios yet." : "No shared scenarios found for this account.")
        : (userIsAccountOwner ? "You haven't created any scenarios yet." : "No public scenarios found for this account.");

    return (
        <>
            <div className={styles.accountScenariosContainer}>
                {hasDisplayableScenariosInitially && (
                    <div className={styles.controlsHeader}>
                        <div className={styles.searchWrapper}>
                            <svg className={styles.searchIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5A6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                            </svg>
                            <input
                                placeholder="Search scenarios or #tags..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className={styles.searchInput}
                                type="search"
                                disabled={!!selectedCategory}
                            />
                        </div>
                        <SortFilterDropdown
                            sortOptions={SORT_OPTIONS}
                            value={sortControls}
                            onChange={handleSortControlsChange}
                        />
                    </div>
                )}

                <div className={styles.categoriesSection}>
                    <div className={styles.categoriesHeader}>
                        <h2 className={styles.sectionTitle}>Categories</h2>
                        {userIsAccountOwner && accountOwner?.categories?.length > 0 && (
                             <p className={styles.categoryEditHint}>Tip: Double-click a category to edit.</p>
                        )}
                    </div>
                    <div className={styles.categoriesList}>
                        {accountOwner?.categories?.map((category) => (
                            <button
                                type="button"
                                key={category._id || category.label}
                                className={`
                                    ${styles.categoryTag}
                                    ${selectedCategory?._id === category._id ? styles.selectedCategory : ''}
                                `}
                                style={{ '--category-color': category.color }}
                                onClick={() => handleCategoryClick(category)}
                                onDoubleClick={() => {
                                    if (userIsAccountOwner) {
                                        handleEditCategoryClick(category);
                                    }
                                }}
                                title={userIsAccountOwner ? `${category.label} (Click to filter, Double-click to edit)` : `${category.label} (Click to filter)`}
                            >
                                {category.label}
                            </button>
                        ))}
                        {userIsAccountOwner && (
                            <button type="button" className={styles.addCategoryButton} onClick={handleAddCategoryClick}>
                                <svg className={styles.addIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                                </svg>
                                Add Category
                            </button>
                        )}
                    </div>
                </div>

                <div className={styles.gridContainer}>
                    {finalRenderScenarios.map((scenario) => (
                        <div key={scenario._id} className={styles.gridItem}>
                            <ScenarioCard
                                scenario={scenario}
                                isAuthor={userIsAccountOwner}
                                onDeleteScenario={handleDeleteScenario}
                                onCopyScenario={handleCopyScenario}
                                searchQuery={searchQuery}
                                isShared={isShared}
                            />
                        </div>
                    ))}
                    {!initialLoading && !hasDisplayableScenariosInitially && (
                        <p className={styles.noResultsMessageFullWidth}>{emptyAccountMessage}</p>
                    )}
                    {!initialLoading && hasDisplayableScenariosInitially && finalRenderScenarios.length === 0 && (
                         <p className={styles.noResultsMessageFullWidth}>No scenarios match your current filters.</p>
                    )}
                </div>
            </div>

            {showDeleteModal && (
                <DeleteScenarioModal
                    handleConfirmDelete={handleConfirmDelete}
                    handleCancelDelete={handleCancelDelete}
                    loading={operationLoading}
                />
            )}
            {showCopyModal && (
                <CopyScenarioModal
                    handleConfirmCopy={handleConfirmCopy}
                    handleConfirmCopyStructure={handleConfirmCopyStructure}
                    handleCancelCopy={handleCancelCopy}
                    loading={operationLoading}
                />
            )}
            {showCategoryModal && (
                <CategoryModal
                    onClose={handleCloseCategoryModal}
                    scenarios={scenarios}
                    accountOwner={accountOwner}
                    setAccountOwner={setAccountOwner}
                    category={editingCategory}
                    setOperationLoading={setOperationLoading}
                />
            )}

            {operationLoading && (
                <>
                    <div className={`${styles.overlay} ${operationLoading ? styles.active : ''}`}></div>
                    <div className={styles.loaderContainer}>
                        <div className={styles.spinner}></div>
                    </div>
                </>
            )}
        </>
    );
};

export default AccountScenarioGrid;
